import {ReactElement, useContext, useState} from "react";
import {Tab, TabList, TabPanel, Tabs} from "@zendeskgarden/react-tabs";
import {AdminWrapper} from "../../AdminWrapper";
import AdminHeader from "../../AdminHeader";
import {Field, Label, Message} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import {CustomZDDropdown} from "../../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {useGetPayRolPositionsQuery} from "../../../../__generated__/generated_types";
import styles from "./PayrollDefaults.module.scss";
import JurisdictionDefaultsForm from "./JurisdictionDefaultsForm";
import PropertyDefaultsForm from "./PropertyDefaultsForm";
import ClientDefaultsWrapper from "./ClientDefaultsWrapper";
import PositionDefaultsByProperty from "./PositionDefaultsByProperty";
import PayrollDefaultsPush from "./PayrollDefaultsPush";
import {Inline} from "@zendeskgarden/react-loaders";
import {PositionFormModal} from "../positions/PositionFormModal";
import {AuthContext} from "../../../../contexts/AuthContext";
import {pushingPayrollDefaultsEnabled} from "../../../../global/feature_checkers";

const subtitles: Record<string, string> = {
    client: "Set organization-wide payroll defaults that apply to all properties.",
    jurisdiction: "Set payroll defaults by jurisdiction to override client-level settings.",
    property: "Set property-specific payroll defaults to override jurisdiction and client-level settings.",
    position: "View effective payroll defaults by property for the selected position.",
    push: "Push configured payroll defaults to employees for the selected position(s).",
};

export function PayrollDefaults(): ReactElement {
    const {user} = useContext(AuthContext);
    const canPushDefaults = pushingPayrollDefaultsEnabled(user);
    const [selectedTab, setSelectedTab] = useState<string>("client");
    const [selectedPositionIds, setSelectedPositionIds] = useState<string[]>([]);
    const [createPositionVisible, setCreatePositionVisible] = useState<boolean>(false);

    const {data: positionsData, loading: positionsDataLoading, refetch: refetchPositions} = useGetPayRolPositionsQuery({
        fetchPolicy: "no-cache"
    });
    const positions = positionsData?.listPayrollPositions ?? [];

    return (
        <AdminWrapper>
            <AdminHeader title="Payroll Defaults" subtitle={subtitles[selectedTab] as string} />
            <div className={styles.positionSelector}>
                <div className={styles.positionSelectorRow}>
                    <Field className={styles.field}>
                        <Label className={styles.label}>Position</Label>
                        <CustomZDDropdown
                            widthSpec={"100%"}
                            applySelectedItems={(items) => {
                                const values = Array.isArray(items) ? items : [items];
                                setSelectedPositionIds(values.filter(Boolean) as string[]);
                            }}
                            initialSelectedItems={selectedPositionIds}
                            openDropdownPlaceholder="Select Position"
                            closedDropdownPlaceholder=""
                            options={positions.map(p => ({value: p.id, label: p.name}))}
                            isError={false}
                            onBlur={() => undefined}
                            isMulti
                            allOption
                            noSearchIcon
                        />
                        {selectedPositionIds.length > 1 && selectedTab !== "push" &&(
                            <Message validation="warning">
                                Saving will overwrite existing defaults for all selected positions.
                            </Message>
                        )}
                        {positionsDataLoading &&
                            <Message>
                                <Inline />
                            </Message>
                        }
                    </Field>
                    <Button
                        isPrimary
                        className={styles.createPositionButton}
                        onClick={() => setCreatePositionVisible(true)}
                    >
                        Add Position
                    </Button>
                </div>
            </div>
            <Tabs onChange={item => setSelectedTab(item)} style={{overflow: "visible"}}>
                <TabList>
                    <Tab item="client">Client</Tab>
                    <Tab item="jurisdiction">Jurisdiction</Tab>
                    <Tab item="property">Property</Tab>
                    <Tab item="position">Position Defaults</Tab>
                    {canPushDefaults &&
                        <Tab item="push" className={styles.pushDefaultsTab}>Push Defaults</Tab>
                    }
                </TabList>
                <TabPanel item="client">
                    {selectedTab === "client" ?
                        <ClientDefaultsWrapper positionIds={selectedPositionIds} /> :
                        <div>Select a position to configure defaults.</div>
                    }
                </TabPanel>
                <TabPanel item="jurisdiction">
                    {selectedTab === "jurisdiction" ?
                        <JurisdictionDefaultsForm positionIds={selectedPositionIds} /> :
                        <div>Select a position to configure jurisdiction defaults.</div>
                    }
                </TabPanel>
                <TabPanel item="property">
                    {selectedTab === "property" ?
                        <PropertyDefaultsForm positionIds={selectedPositionIds} /> :
                        <div>Select a position to configure property defaults.</div>
                    }
                </TabPanel>
                <TabPanel item="position">
                    {selectedTab === "position" ?
                        <PositionDefaultsByProperty positionIds={selectedPositionIds} /> :
                        <div>Select a position to view position defaults.</div>
                    }
                </TabPanel>
                {canPushDefaults &&
                    <TabPanel item="push">
                        {selectedTab === "push" ?
                            <PayrollDefaultsPush positionIds={selectedPositionIds} /> :
                            <div>Select a position to push defaults.</div>
                        }
                    </TabPanel>
                }
            </Tabs>
            {createPositionVisible &&
                <PositionFormModal
                    closed={saved => {
                        setCreatePositionVisible(false);
                        if(saved) {
                            refetchPositions();
                        }
                    }}
                />
            }
        </AdminWrapper>
    );
}
